import { writeBatch, WriteBatch, doc } from 'firebase/firestore';
import { getDb } from './firestoreDb';

/**
 * Shared utilities for batch operations
 */

/**
 * Firestore batch limit is 500 operations
 */
export const BATCH_LIMIT = 500;

/**
 * Safe batch limit (leaving room for safety)
 */
export const SAFE_BATCH_LIMIT = 450;

/**
 * Batch operation manager for handling large batch writes
 */
export class BatchManager {
  private batch: WriteBatch;
  private operationCount: number = 0;

  constructor() {
    this.batch = writeBatch(getDb());
  }

  /**
   * Add an operation to the batch
   * Automatically commits when limit is reached
   */
  async addOperation(operation: (batch: WriteBatch) => void): Promise<void> {
    operation(this.batch);
    this.operationCount++;

    if (this.operationCount >= SAFE_BATCH_LIMIT) {
      await this.commit();
    }
  }

  /**
   * Commit the current batch
   */
  async commit(): Promise<void> {
    if (this.operationCount > 0) {
      await this.batch.commit();
      this.batch = writeBatch(getDb());
      this.operationCount = 0;
    }
  }

  /**
   * Get current operation count
   */
  getOperationCount(): number {
    return this.operationCount;
  }
}

/**
 * Execute operations in batches
 * Automatically handles batching for large operations
 */
export async function executeInBatches<T>(
  items: T[],
  batchSize: number = SAFE_BATCH_LIMIT,
  operation: (batch: WriteBatch, item: T, index: number) => void
): Promise<void> {
  const batchManager = new BatchManager();

  for (let i = 0; i < items.length; i++) {
    await batchManager.addOperation(batch => {
      operation(batch, items[i], i);
    });
  }

  // Commit any remaining operations
  await batchManager.commit();
}

/**
 * Delete documents in batches
 */
export async function deleteInBatches(
  collectionName: string,
  documentIds: string[]
): Promise<void> {
  await executeInBatches(
    documentIds,
    SAFE_BATCH_LIMIT,
    (batch, docId) => {
      const docRef = doc(getDb(), collectionName, docId);
      batch.delete(docRef);
    }
  );
}
